import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePets } from '../context/PetContext';
import { getOwnership } from '../api/client';
import { useCosmeticCatalog } from '../context/CatalogContext';
import { getPetComponent } from '../components/pets';
import { artIdFor } from '../components/pets/ArtPet';
import { CosmeticVisual } from '../components/visuals';
import { getCosmeticComponent, ATTACHMENT_POINTS } from '../components/cosmetics';
import { RELEASE_COSMETIC_PREVIEWS } from '../components/release/releaseCatalog';
import { ArrowIcon, CosmeticsIcon } from '../components/icons';
import './Cosmetics.css';

// Canonical slot order - derived from backend's 10-slot system
const ALL_SLOT_TYPES = ['head', 'ears', 'eyes', 'horns', 'neck', 'back', 'tail', 'feet', 'trail', 'weapon'];

function intToHex(tintInt) {
  if (!tintInt || tintInt === 0) return null;
  const r = (tintInt >> 16) & 0xFF;
  const g = (tintInt >> 8) & 0xFF;
  const b = tintInt & 0xFF;
  return `rgb(${r}, ${g}, ${b})`;
}
const GRID_SIZE = 2; // percentage snap grid for editor (2% = 50 positions per axis)

// Map web slot names to mod uppercase slot names for backend sync
const WEB_TO_MOD_SLOT = {
  head: 'HAT', ears: 'HAT', horns: 'HAT',
  eyes: 'GLASSES', neck: 'COLLAR',
  back: 'BACK_ITEM', tail: 'BACK_ITEM',
  weapon: 'WEAPON', feet: 'BOOTS', trail: 'TRAIL',
};
const MOD_TO_WEB_SLOT = {
  HAT: 'head', GLASSES: 'eyes', COLLAR: 'neck',
  CAPE: 'back', BACK_ITEM: 'back',
  WEAPON: 'weapon', BOOTS: 'feet', TRAIL: 'trail',
};

export default function Cosmetics() {
  const { slot } = useParams();
  const slotNum = parseInt(slot, 10);
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const { pets, equipCosmetic, updatePetStats, userData } = usePets();
  const apiCosmetics = useCosmeticCatalog();
  const [activeTab, setActiveTab] = useState('head');
  const [ownedIds, setOwnedIds] = useState([]);

  // Cosmetics state derived from pet data
  const [localCosmetics, setLocalCosmetics] = useState({});

  // Editor mode
  const [editorMode, setEditorMode] = useState(false);
  const [editorPositions, setEditorPositions] = useState({});
  const [savedPositions, setSavedPositions] = useState({});
  const [dragging, setDragging] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null); // currently selected cosmetic for editing
  // Per-item transforms: { cosmeticId: { rotation: deg, scale: 0.5-2.0, depth: number } }
  const [itemTransforms, setItemTransforms] = useState({});
  const rendererRef = useRef(null);

  // Local tint state for editor mode (not pushed until save)
  const [localTint, setLocalTint] = useState(null); // null = use pet's tint, string = override
  const [savedTint, setSavedTint] = useState(null);

  const pet = pets.find((p) => p.slot_index === slotNum);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  // Sync cosmetics from pet data - this is the source of truth
  // Skip while editor is open to avoid overwriting in-progress edits
  useEffect(() => {
    if (!pet || editorMode) return;
    const equipped = pet.equipped_cosmetics || pet.equippedCosmetics || {};
    // Normalize slot names - mod uses HAT/GLASSES, web uses head/eyes
    const clean = {};
    Object.entries(equipped).forEach(([slotKey, cosmeticId]) => {
      if (cosmeticId && cosmeticId !== 'null' && cosmeticId !== '') {
        const webSlot = MOD_TO_WEB_SLOT[slotKey] || slotKey.toLowerCase();
        clean[webSlot] = cosmeticId;
      }
    });
    setLocalCosmetics(clean);

    // Load custom positions and transforms from pet's cosmetic_positions
    const stored = pet.cosmetic_positions || pet.cosmeticPositions || {};
    const positions = {};
    const transforms = {};
    Object.entries(stored).forEach(([cosmeticId, data]) => {
      if (data.x != null && data.y != null) {
        positions[cosmeticId] = { x: data.x, y: data.y };
      }
      if (data.rotation != null || data.scale != null || data.depth != null) {
        transforms[cosmeticId] = {
          rotation: data.rotation || 0,
          scale: data.scale || 1.0,
          ...(data.depth != null ? { depth: data.depth } : {}),
        };
      }
    });
    setEditorPositions(positions);
    setSavedPositions(positions);
    setItemTransforms(transforms);
  }, [pet, editorMode]);

  // Use backend catalog as single source of truth
  const cosmeticCatalog = apiCosmetics;

  // Build slot lookup from backend catalog: cosmeticId → slot
  const cosmeticSlots = {};
  for (const c of cosmeticCatalog) {
    cosmeticSlots[c.id] = c.slot;
  }

  // Derive visible slot tabs from catalog data
  const SLOT_TYPES = ALL_SLOT_TYPES.filter(s => cosmeticCatalog.some(c => c.slot === s));

  // Fetch actual ownership from backend (checks tier + individual purchases + packages)
  useEffect(() => {
    getOwnership()
      .then(res => {
        const unlocks = res.data.unlocks || [];
        const userTier = res.data.tier || 'free';
        const tierAccess = { free: ['free'], plus: ['free', 'plus'], premium: ['free', 'plus', 'premium'] };
        const accessible = tierAccess[userTier] || tierAccess.free;

        const owned = cosmeticCatalog
          .filter(c => accessible.includes(c.tier) || unlocks.includes(`unlock_${c.id}`))
          .map(c => c.id);
        setOwnedIds(owned);
      })
      .catch(() => {
        // Fallback: show free only
        setOwnedIds(cosmeticCatalog.filter(c => c.tier === 'free').map(c => c.id));
      });
  }, [apiCosmetics]);

  const handleToggle = (cosmeticId) => {
    if (editorMode) return; // Don't toggle while editing positions
    const cosmeticSlot = cosmeticSlots[cosmeticId];
    if (!cosmeticSlot) return;

    const isCurrentlyEquipped = localCosmetics[cosmeticSlot] === cosmeticId;
    const newValue = isCurrentlyEquipped ? null : cosmeticId;

    // Update local state immediately
    const updated = { ...localCosmetics };
    if (newValue) {
      updated[cosmeticSlot] = newValue;
    } else {
      delete updated[cosmeticSlot];
    }
    setLocalCosmetics(updated);

    // Push to backend - use mod-format slot name (HAT, GLASSES, etc.)
    const modSlot = WEB_TO_MOD_SLOT[cosmeticSlot] || cosmeticSlot.toUpperCase();
    equipCosmetic(slotNum, modSlot, newValue);
  };

  const isEquipped = (cosmeticId) => {
    const cosmeticSlot = cosmeticSlots[cosmeticId];
    return cosmeticSlot && localCosmetics[cosmeticSlot] === cosmeticId;
  };

  const isOwned = (cosmeticId) => ownedIds.includes(cosmeticId);

  // ── Editor mode ──────────────────────────────────────────

  // Pet renders at z-index 10. Cosmetics below 10 = behind pet, above 10 = in front.
  const PET_Z = 10;
  const DEFAULT_DEPTH_BACK = 5;   // back-slot items default behind pet
  const DEFAULT_DEPTH_FRONT = 15; // other items default in front

  const getTransform = (cosmeticId) => {
    const defaults = { rotation: 0, scale: 1.0 };
    const slot = cosmeticSlots[cosmeticId] || '';
    defaults.depth = slot === 'back' ? DEFAULT_DEPTH_BACK : DEFAULT_DEPTH_FRONT;
    return { ...defaults, ...(itemTransforms[cosmeticId] || {}) };
  };

  const updateTransform = (cosmeticId, changes) => {
    setItemTransforms(prev => ({
      ...prev,
      [cosmeticId]: { ...getTransform(cosmeticId), ...changes }
    }));
  };

  const enterEditor = () => {
    setEditorMode(true);
    setSavedPositions({ ...editorPositions });
    // Capture current tint so we can restore on cancel
    const currentTint = localCosmetics.tint_color || localCosmetics.TINT_COLOR || null;
    setLocalTint(currentTint);
    setSavedTint(currentTint);
    setSelectedItem(null);
  };

  const cancelEditor = () => {
    setEditorMode(false);
    setEditorPositions({ ...savedPositions });
    // Restore tint to what it was before editing
    if (savedTint !== localTint) {
      setLocalCosmetics(prev => ({ ...prev, tint_color: savedTint }));
    }
    setLocalTint(null);
    setSavedTint(null);
    setSelectedItem(null);
  };

  const saveEditor = async () => {
    setSelectedItem(null);
    setSavedPositions({ ...editorPositions });

    // Merge positions + transforms into cosmetic_positions
    const merged = {};
    for (const [cosmeticId, pos] of Object.entries(editorPositions || {})) {
      merged[cosmeticId] = { ...pos, ...(itemTransforms[cosmeticId] || {}) };
    }
    for (const [cosmeticId, t] of Object.entries(itemTransforms || {})) {
      if (!merged[cosmeticId]) {
        merged[cosmeticId] = { rotation: t.rotation || 0, scale: t.scale || 1.0, ...(t.depth != null ? { depth: t.depth } : {}) };
      }
    }

    // Build a single combined update with positions + tint
    const tintChanged = localTint !== savedTint;
    const changes = { cosmetic_positions: merged };
    if (tintChanged) {
      changes.equipped_cosmetics = {
        ...(pet.equipped_cosmetics || pet.equippedCosmetics || {}),
        TINT_COLOR: localTint || '0',
      };
    }

    // Use updatePetStats for a single atomic push
    await updatePetStats(slotNum, changes);

    setLocalTint(null);
    setSavedTint(null);
    setEditorMode(false);
  };

  const handleEditorMouseDown = useCallback((cosmeticId, e) => {
    if (!editorMode) return;
    e.preventDefault();
    e.stopPropagation();
    setSelectedItem(cosmeticId);
    setDragging(cosmeticId);
  }, [editorMode]);

  const handleEditorMouseMove = useCallback((e) => {
    if (!dragging || !editorMode || !rendererRef.current) return;
    const rect = rendererRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
    const snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;
    const clamped = {
      x: Math.max(0, Math.min(100, snappedX)),
      y: Math.max(0, Math.min(100, snappedY)),
    };
    setEditorPositions((prev) => ({ ...prev, [dragging]: clamped }));
  }, [dragging, editorMode]);

  const handleEditorMouseUp = useCallback(() => {
    setDragging(null);
  }, []);

  // ── Early returns (after all hooks) ──────────────────────

  if (authLoading) return <div className="petview-loading">Loading...</div>;
  if (!pet) {
    return (
      <div className="petview-empty">
        <p>No pet in this slot.</p>
        <button className="btn btn-secondary" onClick={() => navigate('/pets')}>Back to Kennel</button>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────

  const filteredCosmetics = cosmeticCatalog.filter((c) => c.slot === activeTab);
  const PetComponent = getPetComponent(pet.pet_type_id);
  const attachments = ATTACHMENT_POINTS[pet.pet_type_id] || ATTACHMENT_POINTS.default;

  // Get tint color from cosmetics (stored as TINT_COLOR or tint_color)
  const rawTint = localCosmetics.tint_color || localCosmetics.TINT_COLOR || null;
  const tintColor = rawTint && rawTint !== '0' ? intToHex(parseInt(rawTint, 10)) : null;

  // All equipped cosmetics (excluding tint), in a fixed display order by slot
  const SLOT_ORDER = ['back', 'tail', 'feet', 'neck', 'horns', 'head', 'ears', 'eyes', 'trail', 'weapon'];
  const equippedItems = Object.entries(localCosmetics)
    .filter(([k, v]) => v && k !== 'tint_color' && k !== 'TINT_COLOR')
    .map(([, cosmeticId]) => cosmeticId)
    .sort((a, b) => {
      const aIdx = SLOT_ORDER.indexOf(cosmeticSlots[a] || '');
      const bIdx = SLOT_ORDER.indexOf(cosmeticSlots[b] || '');
      return aIdx - bIdx;
    });

  const renderCosmeticLayer = (cosmeticId) => {
    const CosmeticComp = getCosmeticComponent(cosmeticId);
    if (!CosmeticComp) return null;

    const slotKey = Object.entries(localCosmetics).find(([, id]) => id === cosmeticId)?.[0];
    const customPos = editorPositions[cosmeticId];
    const defaultPos = attachments[slotKey] || attachments.head || { x: 50, y: 20 };
    const pos = customPos || defaultPos;
    const t = getTransform(cosmeticId);
    const isSelected = editorMode && selectedItem === cosmeticId;
    const cosmeticSize = 60 * t.scale;

    return (
      <div
        key={cosmeticId}
        className={`cosmetic-layer ${editorMode ? 'cosmetic-layer-editable' : ''} ${dragging === cosmeticId ? 'cosmetic-layer-dragging' : ''} ${isSelected ? 'cosmetic-layer-selected' : ''}`}
        style={{
          position: 'absolute',
          left: `${pos.x}%`,
          top: `${pos.y}%`,
          transform: `translate(-50%, -50%) rotate(${t.rotation}deg)`,
          zIndex: t.depth,
          cursor: editorMode ? 'grab' : 'default',
        }}
        onMouseDown={(e) => handleEditorMouseDown(cosmeticId, e)}
      >
        <CosmeticComp size={cosmeticSize} />
      </div>
    );
  };

  return (
    <div className="cosmetics-page">
      <div className="container">
        <button className="back-btn" onClick={() => navigate(`/pets/${slotNum}`)}>
          <ArrowIcon size={18} /> Back to {pet.pet_name}
        </button>

        <div className="cosmetics-header">
          <CosmeticsIcon size={28} color="var(--magenta-primary)" />
          <h1>Dress Up {pet.pet_name}</h1>
        </div>

        <div className="cosmetics-layout">
          {/* Left: catalog */}
          <div className="cosmetics-catalog">
            <div className="cosmetics-tabs">
              {SLOT_TYPES.map((s) => (
                <button
                  key={s}
                  className={`cosmetics-tab ${activeTab === s ? 'active' : ''}`}
                  onClick={() => setActiveTab(s)}
                >
                  {s.charAt(0).toUpperCase() + s.slice(1)}
                </button>
              ))}
            </div>

            <div className="cosmetics-items">
              {filteredCosmetics.map((item) => {
                const CosmeticPreview = getCosmeticComponent(item.id);
                const previewUrl = RELEASE_COSMETIC_PREVIEWS[item.id];
                const equipped = isEquipped(item.id);
                const owned = isOwned(item.id);

                return (
                  <div
                    key={item.id}
                    className={`cosmetic-item card ${equipped ? 'cosmetic-equipped' : ''} ${!owned ? 'cosmetic-locked' : ''}`}
                    onClick={() => owned && handleToggle(item.id)}
                  >
                    <div className="cosmetic-item-preview">
                      {/* Dressing up happens on the 2D picture, so its lists are 2D too. */}
                      {CosmeticPreview ? (
                        <CosmeticVisual cosmeticId={item.id} size={44} forceMode="2d" />
                      ) : previewUrl ? (
                        <img src={previewUrl} alt={item.name} loading="lazy" className="cosmetic-item-preview-img" />
                      ) : null}
                    </div>
                    <span className="cosmetic-item-name">{item.name}</span>
                    <span className={`tier-badge tier-${item.tier}`}>{item.tier}</span>
                    {!owned && <span className="cosmetic-lock-overlay">Locked</span>}
                    {equipped && <span className="cosmetic-check">Equipped</span>}
                  </div>
                );
              })}
              {filteredCosmetics.length === 0 && (
                <div className="cosmetics-empty-state">
                  <img
                    src="/branding/petlings-pet-128.png"
                    alt=""
                    width={96}
                    height={96}
                    className="cosmetics-empty-img"
                  />
                  <p className="cosmetics-empty-msg">No cosmetics in this category yet.</p>
                </div>
              )}
            </div>
          </div>

          {/* Right: live preview + editor */}
          <div className="cosmetics-preview">
            <div className={`cosmetics-preview-card card ${editorMode ? 'editor-active' : ''}`}>
              <div className="cosmetics-preview-header">
                <h3>{editorMode ? 'Position Editor' : 'Preview'}</h3>
                {!editorMode ? (
                  <button className="btn btn-ghost btn-sm" onClick={enterEditor}>
                    Edit Positions
                  </button>
                ) : (
                  <div className="editor-actions">
                    <button className="btn btn-primary btn-sm" onClick={saveEditor}>Save</button>
                    <button className="btn btn-ghost btn-sm" onClick={cancelEditor}>Cancel</button>
                  </div>
                )}
              </div>

              {editorMode && (
                <div className="editor-controls">
                  {/* Layer list - fixed order, ↑↓ adjusts depth relative to pet */}
                  <div className="editor-layers">
                    <div className="editor-layers-header">
                      <span>Items</span>
                      <span className="editor-layers-hint">↑ forward ↓ back</span>
                    </div>

                    {/* Pet + tint row (non-interactive, shows where the pet sits) */}
                    <div className="editor-layer-row editor-layer-tint">
                      <div className="editor-layer-thumb">
                        {tintColor ? (
                          <div style={{ width: 18, height: 18, borderRadius: 4, background: tintColor }} />
                        ) : (
                          <div style={{ width: 18, height: 18, borderRadius: 4, background: '#888', opacity: 0.3 }} />
                        )}
                      </div>
                      <span className="editor-layer-name">{pet.pet_name || 'Pet'}</span>
                      <span className="editor-layer-depth-label">z:{PET_Z}</span>
                    </div>

                    {/* Cosmetic items - fixed list order, ↑↓ changes depth */}
                    {equippedItems.map((cosmeticId) => {
                      const meta = cosmeticCatalog.find(c => c.id === cosmeticId);
                      const CPreview = getCosmeticComponent(cosmeticId);
                      const isActive = selectedItem === cosmeticId;
                      const t = getTransform(cosmeticId);
                      const isBehind = t.depth < PET_Z;
                      return (
                        <div key={cosmeticId}
                             className={`editor-layer-row ${isActive ? 'active' : ''}`}
                             onClick={() => setSelectedItem(cosmeticId)}>
                          <div className="editor-layer-thumb">
                            {CPreview && <CosmeticVisual cosmeticId={cosmeticId} size={20} forceMode="2d" />}
                          </div>
                          <span className="editor-layer-name">{meta?.name || cosmeticId}</span>
                          <span className={`editor-layer-depth-label ${isBehind ? 'behind' : 'front'}`}>
                            {isBehind ? 'behind' : 'front'}
                          </span>
                          <div className="editor-layer-order">
                            <button title="Bring forward"
                                    onClick={(e) => { e.stopPropagation(); updateTransform(cosmeticId, { depth: t.depth + 1 }); }}>↑</button>
                            <button title="Send backward"
                                    onClick={(e) => { e.stopPropagation(); updateTransform(cosmeticId, { depth: Math.max(0, t.depth - 1) }); }}>↓</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Rotation + Scale for selected item */}
                  {selectedItem && (() => {
                    const t = getTransform(selectedItem);
                    const meta = cosmeticCatalog.find(c => c.id === selectedItem);
                    return (
                      <div className="editor-item-tools">
                        <div className="editor-tools-title">{meta?.name || selectedItem}</div>
                        <div className="editor-tool-row">
                          <span className="editor-tool-label">↻</span>
                          <input type="range" min="-180" max="180" value={t.rotation}
                                 onChange={(e) => updateTransform(selectedItem, { rotation: parseInt(e.target.value) })} />
                          <span className="editor-tool-val">{t.rotation}°</span>
                          <button className="editor-tool-reset" onClick={() => updateTransform(selectedItem, { rotation: 0 })}>⟲</button>
                        </div>
                        <div className="editor-tool-row">
                          <span className="editor-tool-label">⤢</span>
                          <input type="range" min="30" max="200" value={Math.round(t.scale * 100)}
                                 onChange={(e) => updateTransform(selectedItem, { scale: parseInt(e.target.value) / 100 })} />
                          <span className="editor-tool-val">{Math.round(t.scale * 100)}%</span>
                          <button className="editor-tool-reset" onClick={() => updateTransform(selectedItem, { scale: 1.0 })}>⟲</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Pet color tint - rainbow swatch bar */}
              <div className="editor-tint">
                <div className="editor-tint-header">
                  <span>Color Tint</span>
                  <button className="editor-tool-reset"
                          onClick={() => {
                            setLocalTint(null);
                            setLocalCosmetics(prev => {
                              const next = { ...prev };
                              delete next.tint_color;
                              delete next.TINT_COLOR;
                              return next;
                            });
                          }}>Clear</button>
                </div>
                <div className="editor-tint-bar">
                  {Array.from({ length: 24 }).map((_, i) => (
                    <div key={i} className="editor-tint-swatch"
                         style={{ background: `hsl(${i * 15}, 70%, 55%)` }}
                         title={`Hue ${i * 15}°`}
                         onClick={() => {
                           const canvas = document.createElement('canvas');
                           canvas.width = canvas.height = 1;
                           const ctx = canvas.getContext('2d');
                           ctx.fillStyle = `hsl(${i * 15}, 70%, 55%)`;
                           ctx.fillRect(0, 0, 1, 1);
                           const [r, g, b] = ctx.getImageData(0, 0, 1, 1).data;
                           const tint = (0xFF << 24) | (r << 16) | (g << 8) | b;
                           const tintStr = String(tint);
                           setLocalTint(tintStr);
                           setLocalCosmetics(prev => ({ ...prev, tint_color: tintStr }));
                         }}
                    />
                  ))}
                </div>
              </div>

              {/* 2D renderer with cosmetic overlays */}
              <div
                ref={rendererRef}
                className={`cosmetics-renderer ${editorMode ? 'cosmetics-renderer-editor' : ''}`}
                onMouseMove={handleEditorMouseMove}
                onMouseUp={handleEditorMouseUp}
                onMouseLeave={handleEditorMouseUp}
              >
                {/* Grid overlay in editor mode */}
                {editorMode && (
                  <div className="editor-grid">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={`h${i}`} className="editor-grid-line-h" style={{ top: `${(i + 1) * 20}%` }} />
                    ))}
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={`v${i}`} className="editor-grid-line-v" style={{ left: `${(i + 1) * 20}%` }} />
                    ))}
                  </div>
                )}

                {/* All cosmetic layers - each has its own depth z-index */}
                {equippedItems.map((id) => renderCosmeticLayer(id))}

                {/* Pet base at fixed z-index */}
                <div className="cosmetics-pet-base" style={{ zIndex: PET_Z }}>
                  {/* 85% of the canvas, the share the cosmetic anchors assume. */}
                  <PetComponent size={204} color={tintColor || undefined} />
                  {tintColor && !artIdFor(pet.pet_type_id) && <div className="cosmetics-tint-overlay" style={{ background: tintColor }} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
